import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useParams,
} from "react-router-dom";
import { isDemo } from "@/api";
import { Session } from "@/Session";
import { DevSignIn } from "@/pages/DevSignIn";
import { AuthCallback, Login } from "@/pages/Login";
import { Layout } from "@/components/Layout";
import { ThreadsView } from "@/components/ThreadsView";
import { Empty, PageHeader } from "@/components/ui";
import { Access } from "@/pages/Access";
import { Documents } from "@/pages/Documents";
import { InvoiceDetail } from "@/pages/InvoiceDetail";
import { Money } from "@/pages/Money";
import { Overview } from "@/pages/Overview";
import { Profile } from "@/pages/Profile";
import { ProjectFiles } from "@/pages/project/ProjectFiles";
import { ProjectNow } from "@/pages/project/ProjectNow";
import { ProjectShell } from "@/pages/project/ProjectShell";
import { ProjectTalk } from "@/pages/project/ProjectTalk";
import { ProjectTasks } from "@/pages/project/ProjectTasks";
import { ProjectTerms } from "@/pages/project/ProjectTerms";
import { ProjectChanges } from "@/pages/project/ProjectChanges";
import { ProjectMeetings } from "@/pages/project/ProjectMeetings";
import { ProjectReports } from "@/pages/project/ProjectReports";
import { Proposal, ProposalList } from "@/pages/Proposal";
import { Settings } from "@/pages/Settings";
import { ApiDocs } from "@/pages/ApiDocs";
import { StudioClient } from "@/studio/StudioClient";
import { StudioClients } from "@/studio/StudioClients";
import { StudioHome } from "@/studio/StudioHome";
import { StudioInbox } from "@/studio/StudioInbox";
import { StudioLayout } from "@/studio/StudioLayout";
import { StudioMoney } from "@/studio/StudioMoney";
import { StudioProject } from "@/studio/StudioProject";
import { StudioProjectNew } from "@/studio/StudioProjectNew";
import { StudioAudit } from "@/studio/StudioAudit";
import { StudioIntegrations } from "@/studio/StudioAdmin";
import { StudioTeam } from "@/studio/StudioTeam";

function Inbox() {
  const { threadId } = useParams();
  return (
    <>
      <PageHeader
        title="Сообщения"
        description="Вся переписка со студией: общие разговоры по проектам и обсуждения конкретных задач."
      />
      <ThreadsView selectedId={threadId} base="/inbox" />
    </>
  );
}

const router = createBrowserRouter([
  {
    // Studio side. Live: only for staff signed in through the `studio` realm.
    path: "/studio",
    element: <StudioLayout />,
    children: [
      { index: true, element: <StudioHome /> },
      { path: "inbox/:threadId?", element: <StudioInbox /> },
      { path: "clients", element: <StudioClients /> },
      { path: "clients/:clientId", element: <StudioClient /> },
      { path: "projects/new", element: <StudioProjectNew /> },
      {
        path: "projects/:projectId/:tab?/:threadId?",
        element: <StudioProject />,
      },
      { path: "money", element: <StudioMoney /> },
      { path: "team", element: <StudioTeam /> },
      { path: "admin", element: <Navigate to="/studio/admin/audit" replace /> },
      { path: "admin/audit", element: <StudioAudit /> },
      { path: "admin/integrations", element: <StudioIntegrations /> },
      { path: "profile", element: <Profile side="studio" /> },
    ],
  },
  {
    element: <Layout />,
    children: [
      { path: "/", element: <Overview /> },
      {
        path: "/p/:projectId",
        element: <ProjectShell />,
        children: [
          { index: true, element: <ProjectNow /> },
          { path: "tasks/:taskId?", element: <ProjectTasks /> },
          { path: "talk/:threadId?", element: <ProjectTalk /> },
          { path: "reports", element: <ProjectReports /> },
          { path: "meetings", element: <ProjectMeetings /> },
          { path: "changes", element: <ProjectChanges /> },
          { path: "files", element: <ProjectFiles /> },
          { path: "terms", element: <ProjectTerms /> },
        ],
      },
      { path: "/inbox/:threadId?", element: <Inbox /> },
      { path: "/money", element: <Money /> },
      { path: "/money/:invoiceId", element: <InvoiceDetail /> },
      { path: "/documents", element: <Documents /> },
      { path: "/proposals", element: <ProposalList /> },
      { path: "/proposals/:proposalId", element: <Proposal /> },
      { path: "/settings", element: <Settings /> },
      { path: "/settings/api", element: <ApiDocs /> },
      { path: "/profile", element: <Profile side="client" /> },
      { path: "/access", element: <Access /> },
      {
        path: "*",
        element: (
          <Empty title="Такой страницы нет">
            Вернитесь на сводку по ссылке в шапке.
          </Empty>
        ),
      },
    ],
  },
]);

export function App() {
  if (isDemo) return <RouterProvider router={router} />;
  const path = window.location.pathname;
  if (path.startsWith("/login")) return <Login />;
  if (path.startsWith("/auth/callback")) return <AuthCallback />;
  if (path.startsWith("/dev-signin")) return <DevSignIn />;
  return (
    <Session>
      <RouterProvider router={router} />
    </Session>
  );
}
